import { SortingBarItem } from './SortingBarItem';
import { DEFAULT_SORT } from 'config/constants';
import { ProductOrderingModeEnumApi } from 'graphql/generated';
import { getUrlQueriesWithoutDynamicPageQueries } from 'helpers/parsing/urlParsing';
import { useCurrentSortQuery } from 'helpers/queryParams/useCurrentSortQuery';
import { useUpdateSortQuery } from 'helpers/queryParams/useUpdateSortQuery';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useRouter } from 'next/router';
import { SortIcon } from 'public/svg/IconsSvg';
import { useState } from 'react';
import { twJoin } from 'tailwind-merge';
import twMerge from 'utils/twMerge';

type SortingBarProps = {
    sorting: ProductOrderingModeEnumApi | null;
    totalCount: number;
    customSortOptions?: ProductOrderingModeEnumApi[];
};

const DEFAULT_SORT_OPTIONS = [
    ProductOrderingModeEnumApi.PriorityApi,
    ProductOrderingModeEnumApi.PriceAscApi,
    ProductOrderingModeEnumApi.PriceDescApi,
    ProductOrderingModeEnumApi.BestsellingApi,
];

const SortingBar: FC<SortingBarProps> = ({ sorting, totalCount, customSortOptions }) => {
    const router = useRouter();
    const t = useTypedTranslationFunction();
    const updateSort = useUpdateSortQuery();
    const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
    const currentSort = useCurrentSortQuery();

    const asPathWithoutQueryParams = router.asPath.split('?')[0];

    const sortOptionsLabels = {
        [ProductOrderingModeEnumApi.PriorityApi]: t('Recommended'),
        [ProductOrderingModeEnumApi.PriceAscApi]: t('Lowest prices'),
        [ProductOrderingModeEnumApi.PriceDescApi]: t('Highest prices'),
        [ProductOrderingModeEnumApi.BestsellingApi]: t('Bestselling'),
        [ProductOrderingModeEnumApi.RelevanceApi]: t('relevance'),
        [ProductOrderingModeEnumApi.NameAscApi]: t('name ascending'),
        [ProductOrderingModeEnumApi.NameDescApi]: t('name descending'),
    };

    const sortOptions = customSortOptions || DEFAULT_SORT_OPTIONS;
    const selectedSortOption = currentSort || sorting || DEFAULT_SORT;

    const handleSortingBarItemClick = (sortOption: ProductOrderingModeEnumApi) => {
        updateSort(sortOption);
    };

    return (
        <div
            className={twMerge(
                'relative flex w-32 select-none items-center justify-center gap-3 rounded-md bg-grayDark p-3 text-white vl:w-full vl:flex-row vl:justify-between vl:rounded-none vl:bg-opacity-0 vl:p-0 vl:text-black',
                isSortMenuOpen && 'rounded-b-none',
            )}
            onClick={() => setIsSortMenuOpen(!isSortMenuOpen)}
        >
            <SortIcon className="h-4 w-4 align-middle vl:hidden" />
            <div className="flex flex-col justify-center vl:hidden">{t('Sorting')}</div>

            <div
                className={twJoin(
                    'w-full rounded-b-md bg-grayDark vl:static vl:flex vl:gap-8 vl:bg-transparent',
                    isSortMenuOpen ? 'absolute top-full z-[1]' : 'hidden',
                )}
            >
                {sortOptions.map((sortOption) => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { page, ...queriesWithoutPage } = getUrlQueriesWithoutDynamicPageQueries(router.query);
                    const sortParams = new URLSearchParams({
                        ...queriesWithoutPage,
                        sort: sortOption,
                    }).toString();
                    const sortHref = `${asPathWithoutQueryParams}?${sortParams}`;
                    const isSelectedSortOption = sortOption === selectedSortOption;

                    return (
                        <SortingBarItem
                            key={sortOption}
                            href={sortHref}
                            isActive={isSelectedSortOption}
                            onClick={() => handleSortingBarItemClick(sortOption)}
                        >
                            {sortOptionsLabels[sortOption]}
                        </SortingBarItem>
                    );
                })}
            </div>

            <div className="hidden shrink-0 text-tiny text-grayDark vl:block">
                {`${totalCount} `}
                {t('Products count', { count: totalCount })}
            </div>
        </div>
    );
};

export default SortingBar;
